import { get } from 'lodash'
import * as mixpanel from 'mixpanel-browser'
import * as React from 'react'
import { connect } from 'react-redux'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import { bindActionCreators, compose } from 'redux'

import Content from './content'

import SnackbarService from 'services/snackbars'
import * as FormActions from 'store/forms/actions'
import * as PatientsActions from 'store/patients/actions'

import FormType from 'data/questionnaires/FormType'
import IForm from 'data/questionnaires/IForm'
import IQuestionAnswer, {
  AfterFailFormId,
  FormId,
  IGainedAnswers,
  IQuestionsData
} from 'data/questionnaires/IQuestionAnswer'
import IScore from 'data/questionnaires/IScore'

import {
  deleteTempAnswers,
  getFormByTypeAndChild,
  getScore,
  getTempAnswers,
  getUncompletedFormTypes,
  isFormPassed,
  saveScores,
  saveTempAnswers
} from 'services/questionnaires'

import { formTypeRefs } from 'data/questionnaires/FormType'
import { _route } from 'routes/config'
// import { getGamesCount } from 'services/games'

import { IMixpanelProps, IOnlineProps, withMixpanel } from 'components'
import { databaseRef } from 'config/firebase'
import * as moment from 'moment'

interface INativeProps {}

interface IReduxStateProps {
  child: any
  parent: any
  forms: { [type: string]: IForm[] }
  isPlusUser: boolean
  is_in_row: boolean
  questionsData: IQuestionsData
}
interface IReduxDispatchProps {
  PatientsLib: any
  FormLib: any
}

interface IProps
  extends INativeProps,
    IReduxStateProps,
    IReduxDispatchProps,
    IMixpanelProps,
    IOnlineProps,
    RouteComponentProps {}

interface IState {
  form: IForm | null
  activeQuestion: number
  loading: boolean
  passedForm: boolean
  penalty: number
  startQuestion: number
  isFormSubmitted: boolean
}

class Questionnaires extends React.Component<IProps, IState> {
  private static AnswerTimeoutTime = 250

  state: IState = {
    form: null,
    activeQuestion: 0,
    loading: false,
    passedForm: false,
    penalty: 1,
    startQuestion: 1,
    isFormSubmitted: false
  }

  private answerTimeout: number = 0

  componentDidMount() {
    const { child, history } = this.props

    if (!child || !child.patientId) {
      history.replace(_route.child.list)
      return
    }

    this.getForm()
  }

  // if this type of questionnaire is already answered and user has came back to it again (using browser back btn) =>
  // => redirect him to questionnaires page
  componentDidUpdate(prevProps, prevState) {
    const { prevForm } = prevState
    const { form, isFormSubmitted } = this.state

    if (form && prevForm !== form && !isFormSubmitted) {
      const { history, child } = this.props
      const assessment = child.assessments[child.currentAssessment]
      const formTypeAssesment = get(assessment, formTypeRefs[form.type])
      const score = get(formTypeAssesment, [
        'total_results',
        'total_score',
        'score'
      ])

      if (score !== undefined) {
        history.replace(_route.questionnaires(child.patientId))
      }
    }

    if (this.props.is_in_row) {
      const { child } = this.props
      const assessment = child.assessments
      const isAbilityFinished =
        assessment &&
        assessment.hasOwnProperty('have_new_score') &&
        assessment.have_new_score === 0

      if (isAbilityFinished) {
        this.removeHaveNewScoreFromRef()
      }
    }
  }

  componentWillUnmount() {
    const { child, parent } = this.props
    const { passedForm, form } = this.state

    if (!passedForm && form) {
      const { answers, gainedAnswers } = this.getAnswersData()
      saveTempAnswers(
        form,
        parent.id,
        child.patientId,
        child.currentAssessment,
        answers,
        gainedAnswers
      )
    }
  }

  getAnswersData = (
    nextId?: string
  ): {
    activeAnswer: number | null
    answers: IQuestionAnswer[]
    gainedAnswers: IGainedAnswers[]
    attempt: number
  } => {
    const {
      child: { patientId },
      questionsData
    } = this.props

    const formType = this.getFormType()
    const patientData = questionsData[patientId]
    const formTypeData = patientData && formType && patientData[formType]
    const formId = nextId || (formTypeData && formTypeData.currentFormId)
    const formIdData = (formId && formTypeData && formTypeData[formId]) || {}
    const { activeAnswer, answers } = formIdData
    const gainedAnswers = (formTypeData && formTypeData.gainedAnswers) || []
    const attempt = (formTypeData && formTypeData.attempt) || 0

    return {
      activeAnswer: activeAnswer ?? null,
      answers: answers || [],
      gainedAnswers,
      attempt
    }
  }

  getFormFromQuestionsData = (formType: string | null): FormId => {
    const {
      child: { patientId },
      questionsData
    } = this.props
    const patientData = questionsData[patientId]
    const formTypeData = patientData && formType && patientData[formType]
    const formId =
      ((formTypeData && formTypeData.currentFormId) as FormId) || FormId.empty

    return formId
  }

  removeHaveNewScoreFromRef = async () => {
    const id = this.props.child.patientId
    try {
      const childRef = await databaseRef
        .child('children')
        .child(id)
        .child('assessments')
        .child('have_new_score')
      childRef.remove()
    } catch (err) {
      console.error({ err })
    }
  }

  getFormType = (): FormType | null => {
    const {
      location: { state },
      child,
      isPlusUser
    } = this.props

    return (
      (state !== true && (state as FormType)) ||
      (state === true && getUncompletedFormTypes(child, isPlusUser)[0]) ||
      null
    )
  }

  getForm = async (nextAttempt?: boolean) => {
    const { history, child, forms, FormLib, PatientsLib } = this.props
    const { form: stateForm } = this.state
    const { gainedAnswers, attempt: stateAttempt } = this.getAnswersData()
    const attempt = nextAttempt ? stateAttempt + 1 : stateAttempt || 1

    const formType = this.getFormType()

    const formId: FormId = this.getFormFromQuestionsData(formType)

    const nextId = nextAttempt
      ? (stateForm && stateForm.after_fail_form_id) || AfterFailFormId[formId]
      : formId

    const form =
      (!nextAttempt || nextId) &&
      (await getFormByTypeAndChild(formType, child, {
        storage: forms,
        Lib: FormLib,
        nextId,
        attempt,
        startAge: (nextAttempt && stateForm && stateForm.start_age) || undefined
      }))

    if (!form) {
      if (gainedAnswers.length && formType) {
        const data = gainedAnswers[gainedAnswers.length - 1]
        const dataForm = data.form
        const scores =
          dataForm &&
          getScore(data.answers, dataForm, {
            attempt,
            penalty: this.state.penalty
          })
        if (scores) {
          await this.submitScores(scores)
        }
        // this.getForm()
      } else {
        // when user completed last question, pass state in route for showing 'Questinarries Completed' modal (if to start from btn "start questionarries")
        // if games are completed = don't show modal
        // here always will be: formsFinished === true

        // redirect to questionnaires Page and show there modal
        this.setQuestionnarieInRow(() =>
          history.push(_route.questionnaires(child.patientId), {
            showProgrEvalModal: true
          })
        )
      }
      return
    }

    const answerData = this.getAnswersData(nextId)
    const { activeAnswer } = answerData
    let { answers } = answerData

    // old one code,
    // form.penalty from firebase was removed from some child ages
    // const penalty =
    //   attempt === 1 && form.penalty ? form.penalty : this.state.penalty
    let penalty

    // form.penalty (0.6) exists only for 7-8y.o. child
    if (!!form.penalty || this.state.penalty === 0.6) {
      if (attempt === 1) {
        penalty = form.penalty
      } else {
        penalty = this.state.penalty
      }
      // for other 0.075
    } else {
      if (attempt === 1) {
        penalty = this.state.penalty
      } else {
        this.setState({ penalty: 0.75 })
        penalty = this.state.penalty
      }
    }

    if (!answers.length) {
      answers = form.questions.map(
        q => ({ question: q, answer: null } as IQuestionAnswer)
      )
    }

    try {
      const oldAnswers = await this.getAnswers(form)
      if (oldAnswers && oldAnswers.answers) {
        Object.keys(oldAnswers.answers).forEach(qId => {
          // prettier-ignore	DON'T FORMAT THE LINE BELOW. LOOK AT COMMENT BELOW FOR REASON
          // tslint:disable-next-line:triple-equals
          const index = answers.findIndex(a => a.question.id == qId)
          // use == instead of === in case firebase may return number key (if no chars in key) of answer when question id is always string
          if (index < 0) {
            return
          }
          if (!answers.length) {
            answers[index].answer = form.answers[oldAnswers.answers[qId]]
          }
        })
      }
    } catch (error) {
      SnackbarService.showSnackbar('Cannot load previous answers', 'error')
    }

    const activeQuestionIndex = answers.findIndex(a => a.answer === null)
    const activeQuestion =
      activeQuestionIndex < 0 ? form.questions.length - 1 : activeQuestionIndex

    if (formType) {
      PatientsLib.setAnswers(
        {
          formType,
          formTypeData: {
            currentFormId: form.id,
            [form.id]: {
              activeAnswer,
              answers
            },
            gainedAnswers: nextAttempt ? gainedAnswers : [],
            attempt
          }
        },
        () => {
          this.setState({
            form,
            activeQuestion,
            passedForm: false,
            loading: false,
            penalty,
            startQuestion: activeQuestion
          })
        }
      )
    } else {
      console.error('formType is null')
    }
  }

  getAnswers = (form: IForm) => {
    const { parent, child } = this.props
    return getTempAnswers(
      form,
      parent.id,
      child.patientId,
      child.currentAssessment
    )
  }

  setAnswer = (i: number) => {
    const { activeQuestion, form } = this.state
    const { PatientsLib } = this.props
    if (!form) {
      return
    }
    const { answers } = this.getAnswersData()
    const activeAnswer = form.answers[i]
    answers[activeQuestion].answer = activeAnswer
    const formType = this.getFormType()

    if (formType) {
      PatientsLib.setAnswers(
        {
          formType,
          formTypeData: {
            currentFormId: form.id,
            [form.id]: {
              activeAnswer: i,
              answers
            }
          }
        },
        activeQuestion < form.questions.length - 1
          ? this.prepareNextAction
          : undefined
      )
    } else {
      console.error('formType is null')
    }
  }

  prepareNextAction = () => {
    if (this.answerTimeout) {
      clearInterval(this.answerTimeout)
    }
    this.answerTimeout = window.setTimeout(() => {
      this.nextAction()
      this.answerTimeout = 0
    }, Questionnaires.AnswerTimeoutTime)
  }

  nextAction = () => {
    const { activeQuestion, form } = this.state
    const { PatientsLib } = this.props
    if (!form) {
      return
    }
    const { answers } = this.getAnswersData()

    if (activeQuestion === form.questions.length - 1) {
      this.onSubmit()

      return
    }

    const nextQuestionIndex = activeQuestion + 1

    const nextAnswerIndex = form.answers.findIndex(
      a => a === answers[nextQuestionIndex].answer
    )

    const activeAnswer = nextAnswerIndex < 0 ? null : nextAnswerIndex
    const formType = this.getFormType()

    if (formType) {
      PatientsLib.setAnswers(
        {
          formType,
          formTypeData: {
            currentFormId: form.id,
            [form.id]: {
              activeAnswer,
              answers
            }
          }
        },
        this.setState({
          activeQuestion: nextQuestionIndex
        })
      )
    } else {
      console.error('formType is null')
    }
  }

  backAction = (toMainPage: boolean = false) => {
    const { history, child, PatientsLib } = this.props
    const { activeQuestion } = this.state

    if (toMainPage) {
      this.setQuestionnarieInRow(() =>
        history.push(_route.questionnaires(child.patientId))
      )
      return
    }
    const { form } = this.state

    if (!form) {
      return
    }

    const { answers, gainedAnswers } = this.getAnswersData()

    if (activeQuestion === 0) {
      if (gainedAnswers.length) {
        this.goFormBack()
      } else {
        this.setQuestionnarieInRow(() =>
          history.push(_route.questionnaires(child.patientId))
        )
      }
    } else {
      const nextQuestionIndex = activeQuestion - 1
      const nextAnswerIndex = form.answers.findIndex(
        a => a === answers[nextQuestionIndex].answer
      )

      const activeAnswer = nextAnswerIndex < 0 ? null : nextAnswerIndex
      const formType = this.getFormType()

      if (formType) {
        PatientsLib.setAnswers(
          {
            formType,
            formTypeData: {
              currentFormId: form.id,
              [form.id]: {
                activeAnswer,
                answers
              }
            }
          },
          this.setState({
            activeQuestion: activeQuestion - 1
          })
        )
      } else {
        console.error('formType is null')
      }
    }
  }

  goFormBack = async () => {
    const { PatientsLib } = this.props
    const { gainedAnswers, attempt } = this.getAnswersData()
    if (!gainedAnswers || !gainedAnswers.length) {
      return
    }
    const data = gainedAnswers.pop()

    const form = data && data.form
    if (!form || !data) {
      return
    }
    const activeQuestion = form.questions.length - 1
    const answers = data.answers.map(
      (ans, i) =>
        ({
          question: form.questions[i],
          answer: form.answers.find(a => a === ans.answer) || null
        } as IQuestionAnswer)
    )

    const lastAnswer = answers[answers.length - 1].answer
    const answerIndex = lastAnswer && form.answers.indexOf(lastAnswer)
    const activeAnswer = answerIndex && answerIndex < 0 ? null : answerIndex
    const formType = this.getFormType()

    if (formType) {
      PatientsLib.setAnswers(
        {
          formType,
          formTypeData: {
            currentFormId: form.id,
            gainedAnswers,
            attempt: attempt - 1,
            [form.id]: {
              activeAnswer,
              answers
            }
          }
        },
        this.setState({
          form,
          activeQuestion
        })
      )
    } else {
      console.error('formType is null')
    }
  }

  deleteTempAnswers = () => {
    const { child, parent } = this.props
    const { form } = this.state
    if (form) {
      deleteTempAnswers(
        form,
        parent.id,
        child.patientId,
        child.currentAssessment
      )
    }
  }

  onSubmit = async () => {
    const { form, penalty } = this.state
    const { PatientsLib } = this.props

    this.setState({ isFormSubmitted: true })
    this.setState({ loading: true })

    if (!form) {
      SnackbarService.showSnackbar('Something went wrong', 'error')
      return
    }
    const { answers, gainedAnswers, attempt } = this.getAnswersData()
    const scores = getScore(answers, form!, {
      attempt,
      penalty
    })

    const birth = moment.unix(this.props.child?.birth_date)

    mixpanel.identify(this.props.parent.id)
    mixpanel.track('Completed questionnaire', {
      date: moment().unix(),
      name: form?.title,
      'child age': moment().diff(birth, 'years'),
      'child birthdate': this.props.child?.birth_date
    })

    if (!scores) {
      SnackbarService.showSnackbar('Something went wrong', 'error')
      return
    }
    gainedAnswers.push({ form, answers })
    const formType = this.getFormType()
    if (formType) {
      PatientsLib.setAnswers({
        formType,
        formTypeData: {
          gainedAnswers
        }
      })
    }
    if (attempt > 1 || isFormPassed(form, scores)) {
      this.submitScores((await this.getActualScores()) || scores)
    } else {
      this.getForm(true)
    }
  }

  getActualScores = async () => {
    const { form, penalty } = this.state
    const { gainedAnswers, attempt } = this.getAnswersData()
    if (!form) {
      return null
    }
    switch (form!.type) {
      case FormType.fineMotor:
      case FormType.grossMotor:
        const data = gainedAnswers[gainedAnswers.length - 1]
        if (attempt > 1) {
          const dataForm = data.form
          return (
            dataForm && getScore(data.answers, dataForm, { attempt, penalty })
          )
        }
        return null
      default:
        return null
    }
  }

  // this func will trigger after complete the question if user has started it from the question, NOT from the btn "start/continue questionnaires"
  submitScores = async (scores: IScore) => {
    const {
      child,
      location: { state },
      history,
      PatientsLib
    } = this.props
    const { form } = this.state
    const { gainedAnswers } = this.getAnswersData()
    const allForms = state === true ? true : false

    // if (!online) {
    //   SnackbarService.showSnackbar('Internet connection problem', 'error', true)
    // }

    if (form) {
      await saveScores(scores, form, child, gainedAnswers)
    }

    SnackbarService.close()

    PatientsLib.updateCurrentPatient(
      null,
      ({ formsFinished, gamesFinished }: PatientsActions.IUpdChildData) => {
        this.setState({ passedForm: true }, this.deleteTempAnswers)
        if (allForms) {
          const formType = this.getFormType()
          PatientsLib.setAnswers(
            {
              formType,
              formTypeData: {
                gainedAnswers: []
              }
            },
            () => this.getForm()
          )
        } else {
          // redirect to questionnaires Page and show OR not there modal
          this.setQuestionnarieInRow(() =>
            history.push(_route.questionnaires(child.patientId), {
              showProgrEvalModal: formsFinished
            })
          )
        }
      }
    )
    // mixpanel.track('questionnaire complete', {
    //   child_id: child.patientId,
    //   questionnaire_name: form?.title
    // })
  }

  setQuestionnarieInRow = callback => {
    this.props.PatientsLib.UpdateQuestionnarie(false, () => {
      callback()
    })
  }

  getEstimatedTime = () => {
    const { form } = this.state
    const { attempt } = this.getAnswersData()
    if (!form) {
      return 0
    }
    const penalty = 30
    return Math.floor(
      (form.questions.length *
        (form.seconds_per_question - (attempt - 1) * penalty)) /
        60
    )
  }

  render() {
    const { form, activeQuestion, loading } = this.state

    if (!form) {
      return null
    }
    const { activeAnswer } = this.getAnswersData()

    return activeQuestion >= 0 ? (
      <Content
        question={form.questions[activeQuestion]}
        activeQuestion={activeQuestion}
        activeAnswer={activeAnswer}
        questionsCount={form.questions.length}
        answers={form.answers}
        backAction={this.backAction}
        nextAction={this.nextAction}
        setAnswer={this.setAnswer}
        estimatedTime={this.getEstimatedTime()}
        title={form.title}
        loading={loading}
        setInRow={this.setQuestionnarieInRow}
      />
    ) : null
  }
}

const mapStateToProps = (state): IReduxStateProps => ({
  child: state.patients.currentPatient,
  is_in_row: state.patients.questionnarie_is_in_row,
  parent: { ...state.user.data, id: state.user.auth.uid },
  forms: state.forms.allForms,
  isPlusUser: state.plus_user.isPlusUser,
  questionsData: state.patients.questionsData
})

const mapDispatchToProps = (dispatch): IReduxDispatchProps => ({
  PatientsLib: bindActionCreators(PatientsActions, dispatch),
  FormLib: bindActionCreators(FormActions, dispatch)
})

const connector = connect(mapStateToProps, mapDispatchToProps)

const WithHOCs = compose(withRouter, withMixpanel, connector)(Questionnaires)

export default (props: INativeProps) => <WithHOCs {...props} />



// WEBPACK FOOTER //
// ./src/pages/Questionnaires/index.tsx